/**
 * The one phrase list row, shared by the topic screen's "Przydatne zwroty" and
 * the dictionary list — same markup, same hooks, so the two lists can't drift
 * apart (mirrors what `topicRow.js` does for topics).
 *
 * Two independent affordances, and each screen wires its own handlers for them:
 *   - `[data-play-phrase="id"]` — the small round play control; disabled when the
 *     caller has no cached clip for that id.
 *   - `[data-phrase-open="id"]` — the text **and** the trailing chevron (centred
 *     against them), one tap target that opens the phrase preview panel. A real
 *     `<button>`, so Enter/Space come from the browser and no screen needs its own
 *     keyboard handler for it.
 *
 * The `<li>` carries `data-phrase="id"` so a screen can highlight the row the
 * Player reports as current.
 */

import { escapeHtml } from '../utils/html.js';
import { ICONS } from './icons.js';
import { playButtonHtml } from './playButton.js';

/**
 * @param {object} phrase A `phrases` row (or a payload phrase) — `id`, `content`
 *   and `translation` are what gets rendered.
 * @param {?Blob} blob The phrase's cached clip, if the caller has it. Without one
 *   the play control renders disabled.
 */
export function phraseRow(phrase, blob) {
  const id = phrase?.id ?? '';
  const hasAudio = blob instanceof Blob;
  const translation = phrase?.translation
    ? `<p class="mt-0.5 text-sm leading-snug text-neutral-600">${escapeHtml(phrase.translation)}</p>`
    : '';

  return `
    <li data-phrase="${escapeHtml(id)}" class="rounded-xl px-2 py-2 transition">
      <div class="flex items-start gap-1.5">
        ${playButtonHtml({
          hook: `data-play-phrase="${escapeHtml(id)}"`,
          label: 'Odtwórz zwrot',
          hasAudio,
          className: '-mt-0.5',
        })}
        <button type="button" data-phrase-open="${escapeHtml(id)}"
          aria-label="Pokaż szczegóły zwrotu"
          class="flex min-w-0 flex-1 items-center gap-2 text-left">
          <div class="min-w-0 flex-1">
            <p class="font-medium text-[0.95rem] leading-relaxed text-neutral-800">
              ${escapeHtml(phrase?.content ?? '')}
            </p>
            ${translation}
          </div>
          <span class="shrink-0 text-brand/40">${ICONS.chevronRight('h-4 w-4')}</span>
        </button>
      </div>
    </li>`;
}
